import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import api, { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LOGO_URL } from "../lib/brand";
import { TrendingUp, ArrowLeft, ArrowRight, Check, MapPin, Bug, Droplets } from "lucide-react";

const LANGS = [
  { code: "हिंदी", label: "हिन्दी" }, { code: "English", label: "English" },
  { code: "मराठी", label: "मराठी" }, { code: "ગુજરાતી", label: "ગુજરાતી" },
  { code: "ਪੰਜਾਬੀ", label: "ਪੰਜਾਬੀ" }, { code: "తెలుగు", label: "తెలుగు" },
  { code: "ಕನ್ನಡ", label: "ಕನ್ನಡ" },
];
const SUPPORTED = ["हिंदी", "English", "Hinglish"];
const pick = (l, hi, en) => (l === "English" ? en : hi);
const DRAFT = "am_onboard_draft";
const UNITS = [{ k: "bigha", hi: "बीघा", f: 0.625 }, { k: "acre", hi: "एकड़", f: 1 }, { k: "hectare", hi: "हेक्टेयर", f: 2.4711 }];

export default function Onboarding() {
  const { user, guestLogin, setUser } = useAuth();
  const { language, setLanguage } = useApp();
  const navigate = useNavigate();
  const [d, setD] = useState(() => { try { return JSON.parse(localStorage.getItem(DRAFT)) || {}; } catch { return {}; } });
  const [step, setStep] = useState(() => d.step || 0);
  const [crops, setCrops] = useState([]);
  const [chips, setChips] = useState([]);
  const [mandi, setMandi] = useState(null);
  const [payoff, setPayoff] = useState(null);
  const [busy, setBusy] = useState(false);
  const dwell = useRef(Date.now());
  const lang = d.language || language || "हिंदी";

  const save = (patch, nextStep) => {
    const nd = { ...d, ...patch, step: nextStep != null ? nextStep : step };
    setD(nd); localStorage.setItem(DRAFT, JSON.stringify(nd));
    if (nextStep != null) setStep(nextStep);
  };
  const track = (evt, extra = {}) => {
    const secs = Math.round((Date.now() - dwell.current) / 1000);
    api.post("/events", { events: [{ name: evt, props: { step, dwell_seconds: secs, ...extra } }] }).catch(() => {});
    dwell.current = Date.now();
  };
  useEffect(() => { track("onboarding_step_viewed", { step }); /* eslint-disable-next-line */ }, [step]);

  useEffect(() => {
    fetch(`${API_BASE}/public/landing`).then((r) => r.json()).then((j) => setMandi(j?.ticker?.[0] || j?.cards?.[0])).catch(() => {});
    fetch(`${API_BASE}/onboarding/crops?district=${encodeURIComponent(d.district || "Neemuch")}`).then((r) => r.json()).then((j) => setCrops(j.crops || [])).catch(() => {});
  }, [d.district]);
  useEffect(() => {
    if (d.crop) fetch(`${API_BASE}/onboarding/sowing-guesses?crop=${encodeURIComponent(d.crop)}`).then((r) => r.json()).then((j) => setChips(j.chips || [])).catch(() => {});
  }, [d.crop]);
  useEffect(() => {
    if (user && user.id && !user.onboarded && localStorage.getItem(DRAFT) && (d.step || 0) >= 8) finish(false);
    // eslint-disable-next-line
  }, [user]);

  const progress = step <= 1 ? 20 : Math.min(95, 20 + Math.round((step - 1) / 8 * 75));

  const finish = async (viaGoogle) => {
    setBusy(true);
    try {
      if (!user || !user.id) await guestLogin();
      const unit = UNITS.find((u) => u.k === (d.landUnit || "bigha")) || UNITS[0];
      const { data } = await api.post("/onboarding", {
        name: d.name || "", village: d.village || "", district: d.district || "Neemuch", state: "Madhya Pradesh",
        language: SUPPORTED.includes(lang) ? lang : "हिंदी", landArea: d.landArea || 0, landUnit: unit.k,
        crops: d.crop ? [d.crop] : [], sowingDate: d.sowingDate || null, skillMode: d.skillMode || "beginner",
        irrigationSource: "Rain-fed", lat: d.lat, lng: d.lng,
      });
      setUser(data);
      const p = await api.get("/onboarding/payoff").then((r) => r.data).catch(() => null);
      setPayoff(p); setStep(9); localStorage.removeItem(DRAFT);
      track("onboarding_step_completed", { step: 8, viaGoogle });
    } finally { setBusy(false); }
  };

  const googleJoin = () => {
    localStorage.setItem(DRAFT, JSON.stringify({ ...d, step: 8 }));
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/onboarding")}`;
  };
  const goGuestExplore = () => navigate("/explore");
  const back = () => setStep((s) => Math.max(0, s - 1));

  const Shell = ({ children, showBack = true }) => (
    <div className="min-h-screen bg-[#F8FAF6] flex flex-col font-deva">
      {step >= 2 && step <= 8 && (
        <div className="px-5 pt-5">
          <div className="flex items-center gap-3 mb-1">
            {showBack && <button onClick={back} data-testid="onb-back" className="h-9 w-9 rounded-full bg-white border flex items-center justify-center"><ArrowLeft size={17} /></button>}
            <div className="flex-1"><div className="h-2 rounded-full bg-[#E6E9E0] overflow-hidden"><div className="h-full bg-[#1B7A3E] transition-all" style={{ width: `${progress}%` }} /></div></div>
          </div>
          <p className="text-xs text-[#6b7360] pl-12" data-testid="onb-progress">{pick(lang, `आपका खाता ${progress}% तैयार है`, `Your account is ${progress}% ready`)}</p>
        </div>
      )}
      <div className="flex-1 flex flex-col justify-center max-w-[480px] w-full mx-auto px-5 py-6">{children}</div>
    </div>
  );
  const H = ({ children }) => <h1 className="text-2xl font-extrabold text-[#1c2a1e] mb-1 leading-tight">{children}</h1>;
  const Card = ({ onClick, testId, children, active }) => (
    <button onClick={onClick} data-testid={testId} className={`w-full text-left rounded-[14px] p-4 border-2 transition-colors mb-3 ${active ? "border-[#1B7A3E] bg-[#EAF3EC]" : "border-[#E6E9E0] bg-white hover:border-[#c5cdb8]"}`}>{children}</button>
  );

  if (step === 0) return (
    <Shell showBack={false}>
      <img src={LOGO_URL} alt="Agro Madat" width={56} height={56} className="rounded-2xl mb-4" />
      <H>भाषा चुनें · Choose language</H>
      <div className="grid grid-cols-2 gap-2 mt-4" data-testid="onb-langs">
        {LANGS.map((l) => (
          <button key={l.code} data-testid={`onb-lang-${l.code}`}
            onClick={() => { if (SUPPORTED.includes(l.code)) setLanguage(l.code); save({ language: l.code }, 1); }}
            className={`rounded-[12px] py-4 text-lg font-bold border-2 ${lang === l.code ? "border-[#1B7A3E] bg-[#EAF3EC] text-[#1B7A3E]" : "border-[#E6E9E0] bg-white text-[#1c2a1e]"}`}>{l.label}</button>
        ))}
      </div>
    </Shell>
  );

  if (step === 1) return (
    <Shell showBack={false}>
      <p className="text-sm font-semibold text-[#6b7360]">{pick(lang, `${d.district || "नीमच"} मंडी · आज`, `${d.district || "Neemuch"} mandi · today`)}</p>
      <div className="rounded-[16px] bg-white border border-[#E6E9E0] p-5 mt-3" data-testid="onb-mandi">
        <div className="flex items-center gap-2 text-[#1B7A3E] mb-3"><TrendingUp size={20} /><span className="font-bold">{pick(lang, "आज के भाव", "Today's rates")}</span></div>
        <Row name={pick(lang, "सोयाबीन", "Soybean")} price={mandi?.modal || 4850} up />
        <Row name={pick(lang, "लहसुन", "Garlic")} price={8200} up={false} />
        <Row name={pick(lang, "गेहूँ", "Wheat")} price={2415} up />
      </div>
      <p className="text-[13px] text-[#6b7360] mt-3">{pick(lang, "ये रोज़ अपने आप अपडेट होता है।", "This updates automatically every day.")}</p>
      <Button onClick={() => save({}, 2)} data-testid="onb-continue" className="mt-5 h-12 rounded-[12px] bg-[#1B7A3E] text-white font-bold">{pick(lang, "आगे बढ़ें", "Continue")} <ArrowRight size={18} className="ml-1" /></Button>
      <button onClick={goGuestExplore} data-testid="onb-just-rates" className="mt-3 text-sm font-semibold text-[#6b7360] underline">{pick(lang, "बस भाव देखने आया हूं", "Just here for rates")}</button>
    </Shell>
  );

  if (step === 2) return (
    <Shell>
      <H>{pick(lang, "आप कौन हैं?", "Who are you?")}</H>
      <div className="mt-4">
        <Card testId="onb-role-farmer" active={d.role === "farmer"} onClick={() => save({ role: "farmer" }, 3)}><div className="flex items-center gap-3"><span className="text-3xl">🌾</span><p className="font-bold text-[#1c2a1e]">{pick(lang, "मैं खेती करता हूं", "I farm")}</p></div></Card>
        <Card testId="onb-role-buyer" active={d.role === "buyer"} onClick={() => save({ role: "buyer" }, 3)}><div className="flex items-center gap-3"><span className="text-3xl">🏬</span><p className="font-bold text-[#1c2a1e]">{pick(lang, "मैं फसल खरीदता हूं", "I buy crops")}</p></div></Card>
        <Card testId="onb-role-seeker" onClick={goGuestExplore}><div className="flex items-center gap-3"><span className="text-3xl">👀</span><p className="font-bold text-[#1c2a1e]">{pick(lang, "बस देखने आया हूं", "Just looking")}</p></div></Card>
      </div>
    </Shell>
  );

  if (step === 3) return (
    <Shell>
      <H>{d.role === "buyer" ? pick(lang, "व्यक्ति या कंपनी?", "Individual or company?") : pick(lang, "आपका अनुभव?", "Your experience?")}</H>
      <div className="mt-4">
        {d.role === "buyer" ? (
          <>
            <Card testId="onb-buyer-individual" onClick={() => save({ buyerType: "individual" }, 4)}><p className="font-bold text-[#1c2a1e]">{pick(lang, "व्यक्ति", "Individual")}</p></Card>
            <Card testId="onb-buyer-company" onClick={() => save({ buyerType: "company" }, 4)}><p className="font-bold text-[#1c2a1e]">{pick(lang, "कंपनी", "Company")}</p><p className="text-xs text-[#6b7360]">{pick(lang, "GST व फर्म जानकारी बाद में", "GST & firm details later")}</p></Card>
          </>
        ) : (
          <>
            <Card testId="onb-skill-beginner" active={d.skillMode === "beginner"} onClick={() => save({ skillMode: "beginner" }, 4)}><p className="font-bold text-[#1c2a1e]">{pick(lang, "नया हूं, सिखाइए", "New, guide me")}</p><p className="text-xs text-[#6b7360]">{pick(lang, "हर कदम पर सलाह और याद दिलाना मिलेगा", "Advice and reminders at every step")}</p></Card>
            <Card testId="onb-skill-pro" active={d.skillMode === "pro"} onClick={() => save({ skillMode: "pro" }, 4)}><p className="font-bold text-[#1c2a1e]">{pick(lang, "सालों से कर रहा हूं", "Doing it for years")}</p><p className="text-xs text-[#6b7360]">{pick(lang, "सीधे नंबर, कम टोका-टाकी", "Straight numbers, less hand-holding")}</p></Card>
          </>
        )}
      </div>
    </Shell>
  );

  if (step === 4) return (
    <Shell>
      <H>{pick(lang, "आपका खेत कहाँ है?", "Where is your field?")}</H>
      <label className="text-xs font-semibold text-[#6b7360] mt-4 mb-1 block">{pick(lang, "ज़िला", "District")}</label>
      <div className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF3EC] border border-[#C7E1CE] px-3 py-1.5 text-[#1B7A3E] font-semibold w-fit" data-testid="onb-district"><MapPin size={14} />{d.district || "Neemuch"}</div>
      <label className="text-xs font-semibold text-[#6b7360] mt-4 mb-1 block">{pick(lang, "गाँव", "Village")}</label>
      <Input value={d.village || ""} onChange={(e) => save({ village: e.target.value })} placeholder={pick(lang, "गाँव का नाम टाइप करें", "Type your village")} className="rounded-[12px]" data-testid="onb-village" />
      <Button onClick={() => save({}, 5)} data-testid="onb-village-next" className="mt-5 h-12 rounded-[12px] bg-[#1B7A3E] text-white font-bold">{pick(lang, "आगे बढ़ें", "Continue")}</Button>
    </Shell>
  );

  if (step === 5) return (
    <Shell>
      <H>{pick(lang, "क्या बोया है?", "What is sown?")}</H>
      <div className="grid grid-cols-2 gap-2 mt-4" data-testid="onb-crops">
        {crops.map((c) => (
          <button key={c.name} data-testid={`onb-crop-${c.name}`} onClick={() => save({ crop: c.name }, 6)} className={`rounded-[14px] overflow-hidden border-2 ${d.crop === c.name ? "border-[#1B7A3E]" : "border-[#E6E9E0]"}`}>
            <img src={c.photo} alt={c.name} width={200} height={110} loading="lazy" className="w-full h-24 object-cover" />
            <p className="py-2 font-bold text-[14px] text-[#1c2a1e]">{pick(lang, c.name_hi, c.name)}</p>
          </button>
        ))}
      </div>
      <button onClick={() => save({ crop: null }, 8)} data-testid="onb-nothing-sown" className="mt-4 text-sm font-semibold text-[#6b7360] underline">{pick(lang, "अभी कुछ नहीं बोया", "Nothing sown yet")}</button>
    </Shell>
  );

  if (step === 6) return (
    <Shell>
      <H>{pick(lang, "कब बोया था?", "When did you sow?")}</H>
      <div className="mt-4 space-y-3" data-testid="onb-sow-chips">
        {chips.map((c) => <Card key={c.key} testId={`onb-sow-${c.key}`} active={d.sowingDate === c.date} onClick={() => save({ sowingDate: c.date }, 7)}><p className="font-bold text-[#1c2a1e]">{c.label_hi}</p></Card>)}
        <button onClick={() => save({ sowingDate: null }, 7)} data-testid="onb-sow-unknown" className="text-sm text-[#6b7360] underline">{pick(lang, "पक्का याद नहीं", "Not sure")}</button>
      </div>
    </Shell>
  );

  if (step === 7) {
    const unit = UNITS.find((u) => u.k === (d.landUnit || "bigha")) || UNITS[0];
    const area = d.landArea || 2;
    return (
      <Shell>
        <H>{pick(lang, "कितनी ज़मीन?", "How much land?")}</H>
        <div className="flex gap-2 mt-4 mb-4">
          {UNITS.map((u) => <button key={u.k} data-testid={`onb-unit-${u.k}`} onClick={() => save({ landUnit: u.k })} className={`flex-1 py-2 rounded-[10px] border-2 font-semibold text-sm ${(d.landUnit || "bigha") === u.k ? "border-[#1B7A3E] bg-[#EAF3EC] text-[#1B7A3E]" : "border-[#E6E9E0]"}`}>{u.hi}</button>)}
        </div>
        <input type="range" min="0.5" max="50" step="0.5" value={area} onChange={(e) => save({ landArea: Number(e.target.value) })} className="w-full accent-[#1B7A3E]" data-testid="onb-land-slider" />
        <p className="text-center text-2xl font-extrabold text-[#1c2a1e] mt-2">{area} {unit.hi}</p>
        <p className="text-center text-sm text-[#6b7360]">{area} {unit.hi} = {(area * unit.f).toFixed(1)} {pick(lang, "एकड़", "acre")}</p>
        <Button onClick={() => save({}, 8)} data-testid="onb-land-next" className="mt-5 h-12 rounded-[12px] bg-[#1B7A3E] text-white font-bold">{pick(lang, "आगे बढ़ें", "Continue")}</Button>
      </Shell>
    );
  }

  if (step === 8) return (
    <Shell>
      <div className="text-center">
        <div className="h-16 w-16 rounded-full bg-[#EAF3EC] flex items-center justify-center mx-auto mb-4"><Check className="text-[#1B7A3E]" size={30} /></div>
        <H>{pick(lang, "आपका खेत तैयार है!", "Your farm is ready!")}</H>
        <p className="text-sm text-[#6b7360] mt-1">{pick(lang, "इसे याद रखने के लिए Google से जुड़िए।", "Connect with Google to keep it safe.")}</p>
      </div>
      <Button onClick={googleJoin} disabled={busy} data-testid="onb-google" className="mt-6 h-12 rounded-[12px] bg-white border-2 border-[#E6E9E0] text-[#1c2a1e] font-bold hover:bg-[#F8FAF6]">
        <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" />{pick(lang, "Google से जुड़ें", "Connect with Google")}
      </Button>
      <button onClick={() => finish(false)} disabled={busy} data-testid="onb-later" className="mt-3 text-sm font-semibold text-[#6b7360] underline">{busy ? "…" : pick(lang, "अभी नहीं — बाद में", "Not now — later")}</button>
    </Shell>
  );

  return (
    <div className="min-h-screen bg-[#F8FAF6] flex flex-col justify-center max-w-[480px] w-full mx-auto px-5 py-8 font-deva" data-testid="onb-payoff">
      <div className="flex items-center gap-2 text-[#1B7A3E] font-bold mb-2"><Check size={20} /> {pick(lang, "आपका खेत जुड़ गया", "Your farm is connected")}</div>
      {payoff?.hasFarm ? (
        <>
          <h1 className="text-2xl font-extrabold text-[#1c2a1e]">{payoff.crop_hi} · {pick(lang, `${payoff.day}वां दिन`, `day ${payoff.day}`)} · {payoff.stage_hi}</h1>
          <div className="mt-4 space-y-3">
            {payoff.watch_hi && <Line icon={<Bug size={16} className="text-[#B0700A]" />} title={pick(lang, "आज देखने लायक", "Watch today")} text={`${payoff.watch_hi}${payoff.nearbyCount ? pick(lang, ` — इलाके में ${payoff.nearbyCount} किसानों ने बताया`, ` — ${payoff.nearbyCount} nearby reported`) : ""}`} />}
            <Line icon={<Droplets size={16} className="text-[#1C6C93]" />} title={pick(lang, "अगली सिंचाई", "Next irrigation")} text={pick(lang, `लगभग ${payoff.nextIrrigationDays} दिन बाद`, `in ~${payoff.nextIrrigationDays} days`)} />
            {payoff.mandi && <Line icon={<TrendingUp size={16} className="text-[#1B7A3E]" />} title={pick(lang, `${payoff.mandi.market || "मंडी"} आज`, "Mandi today")} text={`${payoff.mandi.commodity} ₹${payoff.mandi.modal?.toLocaleString("en-IN")}/${payoff.mandi.unit}`} />}
          </div>
        </>
      ) : <h1 className="text-2xl font-extrabold text-[#1c2a1e]">{pick(lang, "स्वागत है! चलिए शुरू करें।", "Welcome! Let's begin.")}</h1>}
      <Button onClick={() => navigate("/")} data-testid="onb-enter" className="mt-7 h-12 rounded-[12px] bg-[#1B7A3E] text-white font-bold">{pick(lang, "मेरा खेत खोलें", "Open my farm")}</Button>
    </div>
  );
}

function Row({ name, price, up }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="font-semibold text-[#1c2a1e]">{name}</span>
      <span className="font-bold text-[#1c2a1e]">₹{price.toLocaleString("en-IN")} <span className="text-xs text-[#6b7360]">/क्विंटल</span> <span className={up ? "text-[#1B7A3E]" : "text-[#BC3F1B]"}>{up ? "↑" : "↓"}</span></span>
    </div>
  );
}
function Line({ icon, title, text }) {
  return (
    <div className="flex items-start gap-2 rounded-[12px] bg-white border border-[#E6E9E0] p-3">
      <span className="mt-0.5">{icon}</span>
      <div><p className="text-xs font-semibold text-[#6b7360]">{title}</p><p className="text-[14px] font-semibold text-[#1c2a1e]">{text}</p></div>
    </div>
  );
}
